import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  studentValidation,
  STUDENT_ID_LENGTH,
} from "../../schemas/validationSchema";
import { handleRequest } from "../../http/http";

import workshops from "../../data/workshops.json";
import studyProgrammes from "../../data/studyProgrammes.json";
import terms from "../../data/terms.json";

import Input from "../../components/Inputs/Input";
import InputRadio from "../../components/Inputs/InputRadio";
import { useState } from "react";
import { ParticipantIconText } from "../../components/ParticipantIconText/ParticipantIconText";
import { ParticipantIcon } from "../../icons/ParticipantIcon";
import { jsonCreateStudent } from "../../util/jsonCreate";
import { useNavigate } from "react-router-dom";

import Header from "../../components/Header";
import Captcha from "../../components/Captcha";
import FormPrompt from "../../components/FormPrompt";
import Infos from "../../components/Infos";
import ConfirmModal from "../../components/ConfirmModal";
import scrollToTop from "../../util/scrollToTop";
import { Col, Container, Row } from "react-bootstrap";
import SocialIcons from "../../components/SocialIcons/SocialIcons";

export default function StudentRegistration() {
  const url_student = process.env.REACT_APP_URL_STUDENT_HTTPS;

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields, isSubmitting },
  } = useForm({
    resolver: yupResolver(studentValidation),
  });

  const [select, setSelect] = useState("un");
  const [captchaValue, setCaptchaValue] = useState(null);
  const [errorCaptcha, setErrorCaptcha] = useState("");
  const [onlineError, setOnlineError] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const [sending, setSending] = useState(false);

  const navigate = useNavigate();

  function handleSelectChange(e) {
    setSelect(e.target.value.toLowerCase());
  }

  function handleChange(value) {
    setCaptchaValue(value);
  }

  const onSubmit = (data) => {
    setOnlineError(false);
    if (captchaValue === null) {
      setErrorCaptcha("Morate označiti, da niste robot.");
      return;
    }
    setPendingData(jsonCreateStudent(data));
    setShowConfirm(true);
  };

  const handleConfirmSend = async () => {
    setSending(true);
    setOnlineError(false);
    try {
      const response = await handleRequest(
        url_student,
        pendingData,
        captchaValue
      );
      if (response.status === 200) {
        navigate("/uspesno-poslano/student", {
          replace: true,
          state: pendingData,
        });
      }
    } catch (error) {
      console.warn(error);
      setOnlineError(true);
      setShowConfirm(false);
      scrollToTop();
    } finally {
      setSending(false);
    }
  };

  const handleCancelSend = () => {
    setShowConfirm(false);
  };

  return (
    <>
      <Container fluid>
        <Row>
          <Col md={{ span: 6, offset: 3 }}>
            <Header url=""/>
          </Col>
        </Row>
        <Row>
          <Col md={{ offset: 3, span: 6 }}>
            <div className="title-box">
              <p>Prijava na konferenco OTS</p>
            </div>
          </Col>
        </Row>
        <Row>
          <Col md={{ span: 6, offset: 3 }}>
            <div className="heading">
              <p>Za prijavo na konferenco izpolnite spodnji obrazec.</p>
              <p>
                Po prijavi boste na elektronski naslov prejeli sporočilo s
                potrdilom o prijavi.
              </p>
            </div>
          </Col>
          <Col>
            <SocialIcons />
          </Col>
        </Row>
        <Row>
          <Col md={{ span: 6, offset: 3 }}>
            <form
              className="obrazec"
              id="my-form"
              onSubmit={handleSubmit(onSubmit)}
            >
              <FormPrompt
                hasUnsavedChanges={isDirty}
                dirtyFields={dirtyFields}
              />

              <div className="div">
                <div className="frame">
                  <div className="frame-2">
                    <div className="icon-text-wrapper">
                      <ParticipantIconText
                        text={`UDELEŽENEC / UDELEŽENKA`}
                        className="design-component-instance-node"
                        icon={
                          <ParticipantIcon className="uporabnik-icon-instance" />
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="row-error">
                  <Infos
                    isSubmitting={isSubmitting || sending}
                    onlineError={onlineError}
                  />
                </div>

                <Input
                  label="Ime"
                  labelClass="vertical-align"
                  required={true}
                  inputType="text"
                  id="firstName"
                  name="firstName"
                  placeholder="Ime"
                  error={errors.firstName?.message}
                  register={register}
                />

                <Input
                  label="Priimek"
                  labelClass="vertical-align"
                  required={true}
                  inputType="text"
                  id="lastName"
                  name="lastName"
                  placeholder="Priimek"
                  error={errors.lastName?.message}
                  register={register}
                />

                <Input
                  label="E-naslov"
                  labelClass="vertical-align"
                  required={true}
                  inputType="text"
                  id="email"
                  name="email"
                  placeholder="E-naslov"
                  error={errors.email?.message}
                  register={register}
                />

                <Input
                  label="Vpisna številka"
                  labelClass="vertical-align"
                  required={true}
                  inputType="text"
                  id="studentId"
                  name="studentId"
                  placeholder="Vpisna številka"
                  error={errors.studentId?.message}
                  register={register}
                  maxLength={STUDENT_ID_LENGTH}
                />

                <Row>
                  <Col
                    md={{ offset: 1, span: 4 }}
                    className="d-flex align-items-center"
                  >
                    <label htmlFor="studyLevel" className="vertical-align">
                      Stopnja študija: <span className="required">*</span>
                    </label>
                  </Col>

                  <Col md={6}>
                    <select
                      name="studyLevel"
                      id="studyLevel"
                      {...register("studyLevel")}
                      onChange={handleSelectChange}
                    >
                      <option value="UN">
                        Univerzitetni študijski program
                      </option>
                      <option value="VS">
                        Visokošolski strokovni študijski program
                      </option>
                      <option value="MAG">Magistrski študijski program</option>
                    </select>
                  </Col>
                </Row>
                <Row>
                  <Col
                    md={{ offset: 1, span: 4 }}
                    className="d-flex align-items-center"
                  >
                    <label htmlFor="studyProgramme" className="vertical-align">
                      Študijski program: <span className="required">*</span>
                    </label>
                  </Col>
                  <Col md={6}>
                    <select
                      name="studyProgramme"
                      id="studyProgramme"
                      required
                      {...register("studyProgramme")}
                    >
                      {select !== "" &&
                        studyProgrammes[`${select}`].map((sp, index) => (
                          <option key={index} value={sp.value}>
                            {sp.title}
                          </option>
                        ))}
                    </select>
                  </Col>
                </Row>
                <Row>
                  <Col
                    md={{ offset: 1, span: 4 }}
                    className="d-flex align-items-center"
                  >
                    <label htmlFor="studyYear" className="vertical-align">
                      Letnik študija: <span className="required">*</span>
                    </label>
                  </Col>
                  <Col md={6}>
                    <select
                      name="studyYear"
                      id="studyYear"
                      {...register("studyYear")}
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                      {select !== "mag" && <option value="3">3</option>}
                      <option value="absolvent">Absolvent</option>
                    </select>
                  </Col>
                </Row>

                <InputRadio
                  label="Prijava na delavnico"
                  items={workshops}
                  name={"workshop1"}
                  register={register}
                />

                <Row>
                  <Col md={{ span: 11, offset: 1 }}>
                    <div className="opomba">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 512 512"
                        id="IconChangeColor"
                        style={{ stroke: "#41C1C7" }}
                      >
                        <title>ionicons-v5-a</title>
                        <path
                          d="M448,256c0-106-86-192-192-192S64,150,64,256s86,192,192,192S448,362,448,256Z"
                          style={{
                            fill: "none",
                            stroke: "#41C1C7", // Change stroke color here
                            strokeMiterlimit: 10,
                            strokeWidth: "32px",
                          }}
                          id="mainIconPathAttribute"
                        ></path>
                        <path
                          d="M250.26,166.05,256,288l5.73-121.95a5.74,5.74,0,0,0-5.79-6h0A5.74,5.74,0,0,0,250.26,166.05Z"
                          style={{
                            fill: "none",
                            stroke: "#41C1C7", // Change stroke color here
                            strokeLinecap: "round",
                            strokeLinejoin: "round",
                            strokeWidth: "32px",
                          }}
                          id="mainIconPathAttribute"
                        ></path>
                        <path
                          d="M256,367.91a20,20,0,1,1,20-20A20,20,0,0,1,256,367.91Z"
                          id="mainIconPathAttribute"
                          style={{ fill: "#41C1C7", stroke: "#41C1C7" }} // Change stroke color here
                        ></path>
                      </svg>{" "}
                      <em>
                        V primeru prevelikega števila prijav vas o možnosti
                        udeležbe obvestimo.
                      </em>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={{ span: 11, offset: 1 }}>
                    {terms.map((t, index) => {
                      return (
                        <div key={index}>
                          <label className="checkbox-container">
                            {t.title}
                            <input
                              type="checkbox"
                              id="termsCheckbox"
                              name="terms"
                              value={t.value}
                              {...register("terms")}
                            />
                            <span className="checkmark-checkbox"></span>
                          </label>
                        </div>
                      );
                    })}
                  </Col>
                </Row>
              </div>
              <Captcha handleChange={handleChange} error={errorCaptcha} />
              <Row className="pt-4 d-flex">
                <Col>
                  <div className="d-flex justify-content-center">
                    <input
                      type="submit"
                      className="primary-button"
                      value="Pošlji"
                    />
                  </div>
                </Col>
              </Row>
            </form>
          </Col>
        </Row>
      </Container>
      <ConfirmModal
        show={showConfirm}
        onConfirm={handleConfirmSend}
        onCancel={handleCancelSend}
        sending={sending}
      />
      <img className="bottomImg" src="images/bottom-bg.png" alt="" />
    </>
  );
}
